import React, { useState, useEffect } from 'react'
import styles from './ShortlistContent.module.css'
import JobCard from './JobCard'
import CourseCard from './CourseCard'
import NewsCard from './NewsCard'
import EventCard from './EventCard'
import CustomItemCard from './CustomItemCard'

const SvgSparkle = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 2l2.4 7.2L22 12l-7.6 2.4L12 22l-2.4-7.6L2 12l7.6-2.4L12 2z"/></svg>
const SvgSend    = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
const SvgOk      = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
const SvgWarning = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>

const API_URL = process.env.REACT_APP_API_URL || ''

function getCookie(name) {
    const value = `; ${document.cookie}`
    const parts = value.split(`; ${name}=`)
    if (parts.length === 2) return parts.pop().split(';').shift()
}

const QUICK_SUGGESTIONS = [
    "Focus on Python roles",
    "More remote opportunities",
    "Companies in fintech",
    "English-speaking jobs only",
    "Roles with leadership focus",
    "Senior positions (5+ years)",
    "Design system experience",
    "Startup environments"
]

function parseBullets(reasons) {
    if (!reasons) return []
    try {
        const parsed = typeof reasons === 'string' ? JSON.parse(reasons) : reasons
        if (Array.isArray(parsed)) return parsed
        if (Array.isArray(parsed.bullets)) return parsed.bullets
        return []
    } catch {
        return []
    }
}

const ShortlistContent = () => {
    const [expandedSections, setExpandedSections] = useState({
        jobs: true, courses: true, news: true, events: true, customItems: true
    })
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [jobs, setJobs] = useState([])
    const [courses, setCourses] = useState([])
    const [newsItems, setNewsItems] = useState([])
    const [events, setEvents] = useState([])
    const [customItems, setCustomItems] = useState([])

    // Custom Item Form state
    const [isAddingCustom, setIsAddingCustom] = useState(false)
    const [customTitle, setCustomTitle] = useState('')
    const [customDesc, setCustomDesc] = useState('')
    const [customSaving, setCustomSaving] = useState(false)

    // Intent state
    const [intentText, setIntentText] = useState('')
    const [intentSaving, setIntentSaving] = useState(false)
    const [intentSaved, setIntentSaved] = useState(false)
    const [isRefineExpanded, setIsRefineExpanded] = useState(false)

    const handleSuggestionClick = (suggestion) => {
        setIntentText(prev => {
            const trimmed = prev.trim()
            if (!trimmed) return suggestion
            if (trimmed.toLowerCase().includes(suggestion.toLowerCase())) return prev
            return `${trimmed}, ${suggestion}`
        })
    }

    const saveCustomAction = async () => {
        if (!customTitle.trim() || customSaving) return
        setCustomSaving(true)
        try {
            const token = getCookie('token')
            const res = await fetch(`${API_URL}/feed/custom-action`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify({ title: customTitle, description: customDesc })
            })
            if (res.ok) {
                const data = await res.json()
                // Add to list optimistically
                setCustomItems(prev => [...prev, data.action])
                setCustomTitle('')
                setCustomDesc('')
                setIsAddingCustom(false)
            }
        } catch (err) {
            console.error(err)
        } finally {
            setCustomSaving(false)
        }
    }

    const toggleSection = (section) =>
        setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }))

    const saveIntent = async () => {
        if (!intentText.trim() || intentSaving) return
        setIntentSaving(true)
        setIntentSaved(false)
        try {
            const token = getCookie('token')
            const headers = {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            }
            const res = await fetch(`${API_URL}/feed/user-intent`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ intent_text: intentText }),
            })
            if (res.ok) {
                setIntentSaved(true)
                setIntentText('') // Clear text after success
                setTimeout(() => setIntentSaved(false), 3000)
            }
        } catch (e) {
            console.error('Failed to save intent:', e)
        } finally {
            setIntentSaving(false)
        }
    }

    useEffect(() => {
        const fetchShortlist = async () => {
            setLoading(true)
            setError(null)
            try {
                const token = getCookie('token')
                const headers = token ? { Authorization: `Bearer ${token}` } : {}

                // Single call — backend now JOINs source details inline
                const profileRes = await fetch(`${API_URL}/feed/user-profile`, { headers })
                if (!profileRes.ok) throw new Error(`Profile fetch failed: ${profileRes.status}`)
                const profile = await profileRes.json()

                const shortlist = Array.isArray(profile.shortlist) ? profile.shortlist : []
                const userActions = Array.isArray(profile.actions) ? profile.actions : []

                // Ensure intent is empty by default for fresh submissions
                setIntentText('')

                if (shortlist.length === 0) { setLoading(false); return }

                // Most recent week_start
                const latestWeek = shortlist.reduce((best, row) =>
                    (!best || row.week_start > best) ? row.week_start : best, null)

                const thisWeekItems = shortlist.filter(row => row.week_start === latestWeek)

                if (thisWeekItems.length === 0) { setLoading(false); return }

                const getRelatedItem = (action) => {
                    const ct = action.related_content_type
                    if (!ct) return null
                    const baseItem = { action_id: action.action_id, status: action.status }
                    if (ct === 'job')    return { ...baseItem, title: action.related_job_title,    subtitle: action.related_job_company,       url: action.related_job_url,    type: 'job' }
                    if (ct === 'course') return { ...baseItem, title: action.related_course_title,  subtitle: action.related_course_provider,   url: action.related_course_url,  type: 'course' }
                    if (ct === 'news')   return { ...baseItem, title: action.related_news_title,    subtitle: action.related_news_publisher,    url: action.related_news_url,   type: 'news' }
                    if (ct === 'event')  return { ...baseItem, title: action.related_event_title,   subtitle: action.related_event_location,    url: action.related_event_url,  type: 'event' }
                    return null
                }

                // Build action group map:
                //   "content_type:source_id" → { [action_type]: { groupKey, description, status, primary_content_id, relatedItems[] } }
                const actionGroupMap = {}
                userActions.forEach(action => {
                    const aType     = action.content_type
                    const aSourceId = String(action.source_id)
                    if (!aType || !aSourceId || aSourceId === 'null') return

                    const itemKey   = `${aType}:${aSourceId}`
                    const groupType = action.action_type || 'OTHER'

                    if (!actionGroupMap[itemKey]) actionGroupMap[itemKey] = {}
                    if (!actionGroupMap[itemKey][groupType]) {
                        actionGroupMap[itemKey][groupType] = {
                            action_type:        groupType,
                            description:        action.description || action.title || '',
                            status:             'proposed',
                            primary_content_id: action.primary_content_id,
                            relatedItems:       [],
                        }
                    }
                    
                    if (!action.related_content_id) {
                        actionGroupMap[itemKey][groupType].description = action.description || action.title || ''
                        if (action.status === 'accepted') {
                            actionGroupMap[itemKey][groupType].status = 'accepted'
                        }
                    }

                    // Collect the related content item (e.g. the specific course recommended)
                    const relatedItem = getRelatedItem(action)
                    if (relatedItem && relatedItem.title) {
                        actionGroupMap[itemKey][groupType].relatedItems.push(relatedItem)
                    }
                })

                // Convert inner map to array for each shortlist item
                const getActions = (type, id) =>
                    Object.values(actionGroupMap[`${type}:${String(id)}`] || {})

                const builtJobs = [], builtCourses = [], builtNews = [], builtEvents = []

                thisWeekItems.forEach(row => {
                    const { content_type, source_id, similarity, reasons } = row
                    const match    = Math.round((similarity || 0) * 100)
                    const whyMatch = parseBullets(reasons)
                    const actions  = getActions(content_type, source_id)

                    if (content_type === 'job') {
                        builtJobs.push({
                            id: source_id,
                            title:    row.job_title    || '',
                            company:  row.job_company  || '',
                            location: row.job_location || '',
                            type: 'Full-time',
                            readTime: '',
                            url:  row.job_url || '',
                            match, whyMatch, actions,
                        })
                    } else if (content_type === 'course') {
                        builtCourses.push({
                            id: source_id,
                            title:    row.course_title    || '',
                            provider: row.course_provider || '',
                            duration: row.course_start_at
                                ? new Date(row.course_start_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                                : '',
                            url: row.course_url || '',
                            match, whyMatch, actions,
                        })
                    } else if (content_type === 'news') {
                        builtNews.push({
                            id: source_id,
                            title:       row.news_title        || '',
                            source:      row.news_publisher     || '',
                            publishedAt: row.news_published_at || '',
                            url: row.news_url || '',
                            match, whyMatch, actions,
                        })
                    } else if (content_type === 'event') {
                        builtEvents.push({
                            id: source_id,
                            title:       row.event_title       || '',
                            date: row.event_start_at
                                ? new Date(row.event_start_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                                : '',
                            time: row.event_start_at
                                ? new Date(row.event_start_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
                                : '',
                            location:    row.event_location    || '',
                            description: row.event_description || '',
                            url: row.event_url || '',
                            match, whyMatch, actions,
                        })
                    }
                })

                // Set custom items directly from userActions where action_type === 'CUSTOM'
                const customs = userActions.filter(a => a.action_type === 'CUSTOM')
                setCustomItems(customs)
                
                setJobs([...builtJobs].sort((a, b) => b.match - a.match))
                setCourses(builtCourses)
                setNewsItems(builtNews)
                setEvents(builtEvents)
            } catch (err) {
                console.error('[ShortlistContent] fetch error:', err)
                setError(err.message)
            } finally {
                setLoading(false)
            }
        }

        fetchShortlist()
    }, [])

    if (loading) {
        return (
            <div className={styles.loadingContainer}>
                <div className={styles.spinner} />
                <p className={styles.loadingText}>Loading your weekly shortlist…</p>
            </div>
        )
    }

    if (error) {
        return (
            <div className={styles.errorContainer}>
                <p className={styles.errorText}><SvgWarning /> Could not load shortlist: {error}</p>
            </div>
        )
    }

    const getWeekNumber = (d) => {
        const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
        const dayNum = date.getUTCDay() || 7;
        date.setUTCDate(date.getUTCDate() + 4 - dayNum);
        const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
        return Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
    };

    const weekNumber = getWeekNumber(new Date());
    const noItems = jobs.length + courses.length + newsItems.length + events.length === 0

    return (
        <div className={styles.content}>
            <div className={styles.greeting}>
                <h1 className={styles.greetingTitle}>Your Weekly Shortlist for week {weekNumber}</h1>
                <p className={styles.greetingText}>
                    Top matched jobs, courses, news, and events curated this week — ranked by how well they fit your profile.
                </p>
            </div>

            {/* Refine My Recommendations Section */}
            <div className={styles.intentBox}>
                <div className={styles.intentHeader} onClick={() => setIsRefineExpanded(!isRefineExpanded)}>
                    <div className={styles.intentTitleRow}>
                        <span className={styles.intentIcon}><SvgSparkle /></span>
                        <h3 className={styles.intentLabel}>Refine My Recommendations</h3>
                    </div>
                    <button className={styles.collapseButton}>
                        {isRefineExpanded ? '▼' : '▲'}
                    </button>
                </div>

                {isRefineExpanded && (
                    <div className={styles.intentContent}>
                        <p className={styles.intentSub}>
                            Tell me what you're looking for. Share company URLs, specific skills, job types, or any preferences to help me personalize your shortlist.
                        </p>

                        <div className={styles.suggestionsWrapper}>
                            <p className={styles.suggestionTitle}>Quick suggestions:</p>
                            <div className={styles.suggestionsGrid}>
                                {QUICK_SUGGESTIONS.map((s, idx) => (
                                    <button 
                                        key={idx} 
                                        className={styles.suggestionChip}
                                        onClick={() => handleSuggestionClick(s)}
                                    >
                                        {s}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className={styles.intentInputWrapper}>
                            <textarea
                                className={styles.intentTextarea}
                                rows={3}
                                placeholder="e.g., 'Focus on companies like Stripe and Monzo' or 'More React and TypeScript roles' or paste a company URL..."
                                value={intentText}
                                onChange={e => setIntentText(e.target.value)}
                                onKeyDown={e => { 
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault()
                                        saveIntent() 
                                    }
                                }}
                            />
                            <button
                                className={styles.intentSendBtn}
                                onClick={saveIntent}
                                disabled={intentSaving || !intentText.trim()}
                                title="Press Enter to submit"
                            >
                                {intentSaving ? '...' : intentSaved ? <SvgOk /> : <SvgSend />}
                            </button>
                        </div>
                        <p className={styles.intentHint}>Press Enter to submit, Shift+Enter for new line</p>
                    </div>
                )}
            </div>

            {noItems && (
                <div className={styles.emptyState}>
                    <p>No shortlist items for this week yet. Check back soon!</p>
                </div>
            )}

            {/* Jobs */}
            {jobs.length > 0 && (
                <div className={styles.section}>
                <div className={styles.sectionHeader} onClick={() => toggleSection('jobs')}>
                    <h2 className={styles.sectionTitle}>
                        Jobs <span className={styles.count}>({jobs.length} {jobs.length === 1 ? 'match' : 'matches'})</span>
                    </h2>
                    <button className={styles.collapseButton}>{expandedSections.jobs ? '▼' : '▲'}</button>
                </div>
                    {expandedSections.jobs && (
                        <div className={styles.jobsGrid}>
                            {jobs.map(job => <JobCard key={job.id} job={job} />)}
                        </div>
                    )}
                </div>
            )}

            {/* Courses */}
            {courses.length > 0 && (
                <div className={styles.section}>
                <div className={styles.sectionHeader} onClick={() => toggleSection('courses')}>
                    <h2 className={styles.sectionTitle}>
                        Courses <span className={styles.count}>({courses.length} recommended)</span>
                    </h2>
                    <button className={styles.collapseButton}>{expandedSections.courses ? '▼' : '▲'}</button>
                </div>
                    {expandedSections.courses && (
                        <div className={styles.cardsGrid}>
                            {courses.map(course => <CourseCard key={course.id} course={course} />)}
                        </div>
                    )}
                </div>
            )}

            {/* News */}
            {newsItems.length > 0 && (
                <div className={styles.section}>
                <div className={styles.sectionHeader} onClick={() => toggleSection('news')}>
                    <h2 className={styles.sectionTitle}>
                        Relevant News <span className={styles.count}>({newsItems.length} {newsItems.length === 1 ? 'article' : 'articles'})</span>
                    </h2>
                    <button className={styles.collapseButton}>{expandedSections.news ? '▼' : '▲'}</button>
                </div>
                    {expandedSections.news && (
                        <div className={styles.newsGrid}>
                            {newsItems.map(article => <NewsCard key={article.id} article={article} />)}
                        </div>
                    )}
                </div>
            )}

            {/* Events */}
            {events.length > 0 && (
                <div className={styles.section}>
                <div className={styles.sectionHeader} onClick={() => toggleSection('events')}>
                    <h2 className={styles.sectionTitle}>
                        Attend Relevant Events <span className={styles.count}>({events.length} {events.length === 1 ? 'event' : 'events'})</span>
                    </h2>
                    <button className={styles.collapseButton}>{expandedSections.events ? '▼' : '▲'}</button>
                </div>
                    {expandedSections.events && (
                        <div className={styles.eventsGrid}>
                            {events.map(event => <EventCard key={event.id} event={event} />)}
                        </div>
                    )}
                </div>
            )}

            {/* Custom Items */}
            <div className={styles.section}>
                <div className={styles.sectionHeader} onClick={() => toggleSection('customItems')}>
                    <h2 className={styles.sectionTitle}>
                        Add Your Own Items <span className={styles.count}>({customItems.length} items)</span>
                    </h2>
                    <button className={styles.collapseButton}>{expandedSections.customItems ? '▼' : '▲'}</button>
                </div>
                {expandedSections.customItems && (
                    <div className={styles.customItemsGrid}>
                        {customItems.map((item, idx) => (
                            <div key={item.action_id || idx} className={styles.customItemCard}>
                                <h4>{item.title}</h4>
                                {item.description && <p>{item.description}</p>}
                                <span className={styles.customItemStatus}><SvgOk /> Added</span>
                            </div>
                        ))}
                        
                        {!isAddingCustom ? (
                            <button className={styles.addItemButton} onClick={() => setIsAddingCustom(true)}>
                                <span className={styles.plusIcon}>+</span>
                                Add Your Own Item
                            </button>
                        ) : (
                            <div className={styles.customItemForm}>
                                <input 
                                    className={styles.customInput} 
                                    placeholder="Action title..." 
                                    value={customTitle} 
                                    onChange={e => setCustomTitle(e.target.value)} 
                                    maxLength={100}
                                />
                                <textarea 
                                    className={styles.customTextarea} 
                                    placeholder="Optional description..." 
                                    value={customDesc} 
                                    onChange={e => setCustomDesc(e.target.value)}
                                    rows={2}
                                    maxLength={300}
                                />
                                <div className={styles.customFormActions}>
                                    <button 
                                        className={styles.cancelCustomBtn} 
                                        onClick={() => {
                                            setIsAddingCustom(false)
                                            setCustomTitle('')
                                            setCustomDesc('')
                                        }}
                                        disabled={customSaving}
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        className={styles.saveCustomBtn} 
                                        onClick={saveCustomAction} 
                                        disabled={!customTitle.trim() || customSaving}
                                    >
                                        {customSaving ? 'Saving...' : 'Save Item'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}

export default ShortlistContent
